import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  MenuItem,
  FormControl,
  Select,
  InputLabel
} from '@mui/material';
import { 
  Close as CloseIcon, 
  Send as SendIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../services/api';
import { TEACHERS_STUDENTS } from '../config/Config';
import { sendNudge } from '../features/nudges/api.js';
import EmojiPicker from './EmojiPicker';

const NudgeDialog = ({ open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset form state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedStudentId('');
      setMessage('');
      setError('');
      setLoading(false);
    } else {
      fetchStudents();
    }
  }, [open]);

  const fetchStudents = async () => {
    try {
      setStudentsLoading(true);
      setError(''); // Clear any previous errors
      
      const response = await api.get(TEACHERS_STUDENTS);
      const raw = Array.isArray(response.data) ? response.data : (response.data.results || response.data.students || []);
      
      // Filter active and accepted students
      // Note: is_accepted can be 'accepted' (string) or true (boolean) depending on the API
      const activeStudents = raw
        .filter(r => r.is_active && (r.is_accepted === 'accepted' || r.is_accepted === true))
        .map(r => ({
          id: r.student || r.student_id,
          name: r.student_name || r.student_email || 'Unknown Student',
          email: r.student_email || r.email || '',
          subject: r.subject_name || r.subject?.name || 'No Subject',
          relationship_id: r.id
        }));
      
      setStudents(activeStudents);
      
      // Only set error if API call succeeded but no students found
      // (This is informational, not an error state)
      if (activeStudents.length === 0 && raw.length === 0) {
        // No students at all - this is expected if no relationships exist
        setError('');
      }
    } catch (err) {
      console.error('Error fetching students:', err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.error || err.message || 'Failed to fetch students. Please try again.';
      setError(errorMessage);
      setStudents([]); // Clear students on error
    } finally {
      setStudentsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedStudentId) {
      setError('Please select a student');
      return;
    }

    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    if (message.trim().length > 30) {
      setError('Message must be 30 characters or less');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await sendNudge(selectedStudentId, message.trim());
      
      enqueueSnackbar('Message sent successfully!', { 
        variant: 'success',
        autoHideDuration: 3000
      });
      
      // Reset form
      setSelectedStudentId('');
      setMessage('');
      onClose();
      
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to send message. Please try again.';
      setError(errorMsg);
      enqueueSnackbar(errorMsg, { 
        variant: 'error',
        autoHideDuration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStudentChange = (event) => {
    setSelectedStudentId(event.target.value);
    setError('');
  };

  const selectedStudent = students.find(student => student.id === selectedStudentId);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '400px',
          // Ensure Dialog doesn't blur its children (like Popover)
          filter: 'none',
          backdropFilter: 'none',
        }
      }}
      BackdropProps={{
        sx: {
          // Only blur the backdrop, not the dialog content
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }
      }}
    >
             <DialogTitle sx={{ 
         display: 'flex', 
         alignItems: 'center', 
         justifyContent: 'space-between',
         pb: 1
       }}>
         <Typography variant="h6">Share Love and Encouragement</Typography>
         <IconButton onClick={onClose} size="small">
           <CloseIcon />
         </IconButton>
       </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Student Selection Dropdown */}
        <Box sx={{ mb: 3 }}>
          {studentsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : students.length === 0 && !error ? (
            // No error, but no students found - informational message
            <Alert severity="info">
              No students found. Make sure you have active student relationships.
            </Alert>
          ) : students.length > 0 ? (
            <FormControl fullWidth>
              <InputLabel>Select Student</InputLabel>
              <Select
                value={selectedStudentId}
                onChange={handleStudentChange}
                label="Select Student"
                disabled={loading}
              >
                {students.map((student) => (
                  <MenuItem key={student.id} value={student.id}>
                    {student.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
        </Box>

        {/* Message Input */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your quick message here..."
              variant="outlined"
              inputProps={{ maxLength: 30 }}
              helperText={`${message.length}/30 characters`}
              error={message.length > 30}
              disabled={!selectedStudentId || loading}
            />
            <Box sx={{ pt: 1 }}>
              <EmojiPicker
                onEmojiSelect={(emoji) => {
                  const newMessage = message + emoji;
                  if (newMessage.length <= 30) {
                    setMessage(newMessage);
                  }
                }}
                disabled={!selectedStudentId || loading}
              />
            </Box>
          </Box>
        </Box>

        {selectedStudent && (
          <Box sx={{ 
            p: 2, 
            bgcolor: 'primary.50', 
            borderRadius: 1,
            border: 1,
            borderColor: 'primary.200'
          }}>
            <Typography variant="body2" color="primary.main">
              <strong>Sending to:</strong> {selectedStudent.email}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!selectedStudentId || !message.trim() || message.length > 30 || loading}
          startIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}
        >
          {loading ? 'Sending...' : 'Send Message'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NudgeDialog;
